"use client";

import { create } from "zustand";
import { auth, setTokens, clearTokens, getStoredRefreshToken, type AuthUser } from "./api";

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  hydrate: () => void;
  setUser: (user: AuthUser) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,

  login: async (email, password) => {
    const result = await auth.login(email, password);
    setTokens(result.accessToken, result.refreshToken);
    set({ user: result.user });
  },

  register: async (email, password, name) => {
    const result = await auth.register(email, password, name);
    setTokens(result.accessToken, result.refreshToken);
    set({ user: result.user });
  },

  loginWithGoogle: async (idToken) => {
    const result = await auth.loginWithGoogle(idToken);
    setTokens(result.accessToken, result.refreshToken);
    set({ user: result.user });
  },

  logout: async () => {
    const rt = getStoredRefreshToken();
    if (rt) {
      try {
        await auth.logout(rt);
      } catch {
        // ignore — clear locally regardless
      }
    }
    clearTokens();
    set({ user: null });
  },

  setUser: (user) => set({ user }),

  hydrate: () => {
    const rt = getStoredRefreshToken();
    if (!rt) {
      set({ isLoading: false });
      return;
    }
    auth
      .refresh(rt)
      .then((data) => {
        if (data?.accessToken) {
          setTokens(data.accessToken, rt);
        } else {
          clearTokens();
        }
        set({ isLoading: false });
      })
      .catch(() => {
        clearTokens();
        set({ isLoading: false });
      });
  },
}));
